/**
 * ImageMosaic Component
 *
 * Diamond Architecture: Desktop image grid matching Rails Listings::Media::MosaicComponent.
 * Layout: 1 large hero + 2 smaller images in a responsive grid.
 *
 * Grid structure:
 * ┌─────────────────────────┬──────────┐
 * │                         │  Image 2 │
 * │       Hero Image        ├──────────┤
 * │        (large)          │  Image 3 │
 * └─────────────────────────┴──────────┘
 *          [View All Images]
 */
import { useTranslations } from '@/shared/i18n/useTranslations';
import { plainText, type LocalizedText, type S3Key } from '@letsgethome/types/branded';
import { Button } from '@letsgethome/ui/button';
import { CardControlLayer } from '@letsgethome/ui/card-layers';
import { cn } from '@letsgethome/ui/lib/utils';
import { S3Image } from '@letsgethome/ui/s3-image';
import { UnstyledButton } from '@letsgethome/ui/unstyled-button';
import { Z_LAYERS } from '@letsgethome/ui/z-layers';

interface ImageMosaicProps {
  images: S3Key[];
  alt: LocalizedText | '';
  onViewAll: () => void;
  /**
   * The media query at which this mosaic is the visible layout (its parent
   * hides it otherwise). The hero's preload is scoped to it and its `<img>`
   * stays lazy, so a width that hides the mosaic fetches none of it.
   */
  visibleMedia: string;
  className?: string;
}

export function ImageMosaic({ images, alt, onViewAll, visibleMedia, className }: ImageMosaicProps) {
  const t = useTranslations('listings.gallery');
  const tA11y = useTranslations('common');

  // Every tile opens the gallery through a REAL control layered over the
  // image — the `ListingGallery` shape — not an `onClick` on the `<img>`.
  // The image-with-handler was a phantom control: styled `cursor: pointer`,
  // dead until React hydrated, and invisible to every gate that asks "what
  // would the browser act on here?" (the link-parity phantom probe reported
  // exactly it, `img.bg-muted.object-cover.cursor-pointer`, 2026-09-02). A
  // `<button>` from `packages/ui` is also gated `disabled` until hydration,
  // so the pre-hydration state is honest rather than silently dead (LET-78).
  //
  // The overlay takes its layer from the card-layers seam, and the "View all"
  // button sits in `CardControlLayer` above it — the same overlay/control
  // relation `ListingGallery` and every card compose (LET-85), stated once.
  const openGallery = (
    <UnstyledButton
      type="button"
      className={cn('absolute inset-0 cursor-pointer', Z_LAYERS.cardOverlayLink.className)}
      onClick={onViewAll}
      aria-label={tA11y('a11y.viewAllImages')}
    />
  );

  const [hero, second, third] = images;
  if (!hero) return null;
  const hasSecond = !!second;
  const hasThird = !!third;
  const imageCount = images.length;

  // If only 1 image, render full-width
  if (!hasSecond) {
    return (
      <div className={cn('relative aspect-[20/9] overflow-hidden rounded-xl', className)}>
        <S3Image
          src={hero}
          alt={plainText(alt)}
          fill
          preload={{ media: visibleMedia }}
          className="object-cover"
          sizes="(max-width: 1200px) 100vw, 1200px"
        />
        {openGallery}
        {imageCount > 1 && (
          <ViewAllButton count={imageCount} onClick={onViewAll} label={t('viewAll')} />
        )}
      </div>
    );
  }

  return (
    <div className={cn('relative grid grid-cols-12 grid-rows-2 gap-1.5 aspect-[20/9]', className)}>
      {/* Hero (Left column - 8/12, full height) */}
      <div className="col-span-8 row-span-2 relative overflow-hidden rounded-l-xl">
        <S3Image
          src={hero}
          alt={plainText(`${alt} - main`)}
          fill
          preload={{ media: visibleMedia }}
          className="object-cover"
          sizes="(max-width: 768px) 100vw, 66vw"
        />
        {openGallery}
      </div>
      {/* Right column */}
      {hasThird ? (
        // Stacked: 2 images
        <>
          <div className="col-span-4 row-span-1 relative overflow-hidden rounded-tr-xl">
            <S3Image
              src={second}
              alt={plainText(`${alt} - 2`)}
              fill
              className="object-cover"
              sizes="(max-width: 768px) 50vw, 33vw"
            />
            {openGallery}
          </div>
          <div className="col-span-4 row-span-1 relative overflow-hidden rounded-br-xl">
            <S3Image
              src={third}
              alt={plainText(`${alt} - 3`)}
              fill
              className="object-cover"
              sizes="(max-width: 768px) 50vw, 33vw"
            />
            {openGallery}
          </div>
        </>
      ) : (
        // Single tall image (only 2 images total)
        <div className="col-span-4 row-span-2 relative overflow-hidden rounded-r-xl">
          <S3Image
            src={second}
            alt={plainText(`${alt} - 2`)}
            fill
            className="object-cover"
            sizes="(max-width: 768px) 50vw, 33vw"
          />
          {openGallery}
        </div>
      )}
      {/* View All Button */}
      {imageCount > 3 && (
        <ViewAllButton count={imageCount} onClick={onViewAll} label={t('viewAll')} />
      )}
    </div>
  );
}

function ViewAllButton({
  count,
  onClick,
  label,
}: {
  count: number;
  onClick: () => void;
  label: LocalizedText;
}) {
  return (
    <CardControlLayer className="bottom-3 right-3">
      <Button
        variant="secondary"
        size="sm"
        className="rounded-full bg-white/90 shadow-md hover:bg-white"
        onClick={onClick}
      >
        {/* eslint-disable-next-line i18next/no-literal-string -- formatting: parentheses around dynamic count */}
        {label} ({count})
      </Button>
    </CardControlLayer>
  );
}
